/**
 * The header search: an icon that becomes a field, with live results under it
 * (step 09 task 5, `plan.md` §38.1).
 *
 * Collapsed by default because the bar has a brand, a menu and an identity in
 * it already, and a permanent input at 390px leaves room for none of them.
 * Open, it takes the width it needs and pushes nothing off.
 *
 * **Results arrive as you type** — debounced, from two characters — in a
 * dropdown grouped Raves / Artists. It is the ARIA combobox pattern: the field
 * keeps focus, the arrow keys move `aria-activedescendant` through the
 * options, Enter opens the highlighted one and Escape closes the list. Enter
 * with nothing highlighted still goes to the full results page,
 * `/raves?q=`, which is where a search that wants more than a glance lands.
 *
 * **`/search` needs a session** (§9.1), and this renders only inside
 * `AppShell`, which only signed-in hosts wear. The query is ALSO held back
 * until there is a user, so a shell that ever rendered signed out would ask
 * nothing rather than collect a 401.
 *
 * **Inside a rave it searches THAT rave** — the edition the rave published
 * through `raveSearchScope.ts`, from its bundle, with no request, so it works
 * offline. The list always ends with "Search all of MyRave for …", which
 * opens all of MyRave's results as a full page OVER the page the person is
 * on, on that host (`SearchPage.tsx`; the user, 2026-11-02: "the search page
 * should be displayed where the user is, don't take it anywhere else") — the
 * field keeps driving it; ✕, Escape or another page closes it. Offered only
 * with a session, because the platform's search needs one (§9.1). Enter on
 * the platform's own dropdown opens the same page. **A rave has a results
 * page too** (the user, 2026-11-02: "search on this rave also should open
 * search page results"): Enter, "Show all N acts", a genre, a country, or
 * "Search {rave} for …" from a page open it — the rave draws it
 * (`visitor/RaveResults.tsx`), the header writes the words into
 * `showRaveResults` as they are typed.
 *
 * **On a page with a list of its own it searches THAT page** (the user,
 * 2026-11-02: "where there is a search inside the page, remove it and make
 * the global search in the header act the same inside the page") — the page
 * publishes its query through `pageSearchScope.ts`, the field shows it and
 * typing filters the page live. The dropdown then holds only the way out to
 * the wider searches — the rave or the console, which switch the field and
 * offer "Search only this page" back, and all of MyRave's results page.
 */
import { Fragment, Suspense, lazy, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { endpoints, type SearchResults } from '@/lib/api';
import { appUrl, festivalUrl, isAppHost } from '@/lib/tenant';
import { flag } from '@/ui';
import { useT } from '@/i18n';
import { useAuth, leaveTo } from './AuthProvider';
import { showRaveResults, useRaveResults, useRaveSearchScope, type RaveHit } from './raveSearchScope';
import { useConsoleSearchScope, type ConsoleHit } from './consoleSearchScope';
import { usePageSearch } from './pageSearchScope';
import { ResultsLoading, ResultsPage } from './ResultsPage';
import './HeaderSearch.css';

/** All of MyRave's results page — the raves' cards come with it, so it loads when it opens. */
const loadSearchPage = () => import('./SearchPage');
const SearchPage = lazy(loadSearchPage);

/** How long typing must pause before a request goes out. */
export const SEARCH_DEBOUNCE_MS = 250;
/** Fewer characters than this matches too much to be worth asking about. */
export const SEARCH_MIN_CHARS = 2;
/** Per group. The dropdown is a glance; the page has the rest. */
const PER_GROUP = 5;

type Option =
  | { kind: 'rave'; id: string; label: string; detail: string | null; subdomain: string; editionSlug?: string }
  | { kind: 'artist'; id: string; label: string; detail: string | null; slug: string; flags: string }
  /** A row of the open rave's own results. */
  | { kind: 'local'; id: string; label: string; detail: string | null; hit: RaveHit }
  /** Switch between this page, this rave or the console, and all of MyRave. */
  | { kind: 'scope'; id: string; label: string; detail: null; to: Choice }
  /** All of MyRave: the full results page. */
  | { kind: 'all'; id: string; label: string; detail: null }
  /** This rave: its results page. */
  | { kind: 'rave-page'; id: string; label: string; detail: null }
  /** A row of the admin console's results (2026-10-09). */
  | { kind: 'console'; id: string; label: string; detail: string | null; hit: ConsoleHit };

/** Which search the field is on: the page's, or the rave's or the console's. */
type Choice = 'page' | 'scope';

const localOption = (hit: RaveHit): Option => ({
  kind: 'local', id: `${hit.kind}-${hit.key}`, label: hit.label, detail: hit.detail, hit,
});

/** The latest value once it has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

function optionsOf(data: SearchResults | undefined): { raves: Option[]; artists: Option[] } {
  const raves: Option[] = (data?.festivals ?? []).slice(0, PER_GROUP).map((f) => ({
    kind: 'rave', id: `rave-${f.id}-${f.editionSlug ?? ''}`, label: f.name,
    detail: [f.editionName, f.venue].filter(Boolean).join(' · ') || null,
    subdomain: f.subdomain,
    // Each year is its own rave: a result opens ITS year, not the default.
    editionSlug: f.editionSlug,
  }));
  const artists: Option[] = (data?.performers ?? []).slice(0, PER_GROUP).map((p) => ({
    kind: 'artist', id: `artist-${p.id}`, label: p.name,
    detail: p.tags.length ? p.tags.join(' · ') : null,
    slug: p.slug, flags: p.countries.map(flag).join(''),
  }));
  return { raves, artists };
}

/** The space the opened bar leaves after the logos it stops at. */
const FIND_GAP = 8;

type Box = Pick<DOMRect, 'left' | 'right'>;
/**
 * The opened bar on a phone: from `FIND_GAP` past the logos' end edge to the header bar's end edge. `reach` is its
 * width, `end` how far it runs past its own icon's end edge (logical: left in Hebrew). Pure, so it is tested.
 */
export function findSpan(icon: Box, logos: Box, bar: Box, rtl: boolean): { reach: number; end: number } {
  const start = rtl ? logos.left - FIND_GAP : logos.right + FIND_GAP;
  const end = rtl ? Math.max(0, icon.left - bar.left) : Math.max(0, bar.right - icon.right);
  const reach = rtl ? start - bar.left : bar.right - start;
  return { reach: Math.max(38, Math.round(reach)), end: Math.round(end) };
}

/**
 * `field`: always open, as the admins' header draws it (the Stitch design of
 * 2026-10-09) — a search box, not an icon that grows into one.
 */
export function HeaderSearch({ field = false }: { field?: boolean } = {}) {
  const tr = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpenRaw] = useState(field);
  const setOpen = (v: boolean) => setOpenRaw(field || v);
  const [own, setOwn] = useState('');
  const [focused, setFocused] = useState(false);
  /** Escape closed the list; typing again reopens it. */
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  /** The rave the header is standing in, or null on the platform's pages. */
  const scope = useRaveSearchScope();
  /** On the admin console it searches the console (the user, 2026-10-09). */
  const consoleScope = useConsoleSearchScope();
  /** A page with a list of its own (2026-11-02). */
  const page = usePageSearch();
  /** What somebody switched to; null is the nearest — the page, else the rave or the console, else MyRave. */
  const [choice, setChoice] = useState<Choice | null>(null);
  useEffect(() => { setChoice(null); }, [scope, consoleScope, page?.id]);
  const onPage = Boolean(page) && (choice === null || choice === 'page');
  const inRave = Boolean(scope) && !onPage;
  const inConsole = Boolean(consoleScope) && !scope && !onPage;
  /**
   * A results page, open — the TWO searches (the user, 2026-11-02: "1 search inside the rave (should work offline),
   * 1 search all MyRave (online only)"): the rave's, drawn by the rave, or all of MyRave's, drawn here. The field
   * writes its words into whichever is open; its tabs switch between them.
   */
  const resultsPage = useRaveResults();
  const everywhere = resultsPage?.where === 'all';
  const ravePage = resultsPage?.where === 'rave';
  /** On a page the field IS the page's search: its words are the page's state. */
  const q = onPage && page ? page.query : own;
  const setQ = (v: string) => { if (onPage && page) page.setQuery(v); else setOwn(v); };
  /** A page opened already filtered (a `?q=`) shows its words, even on a phone. */
  const shown = open || (onPage && Boolean(q));

  useEffect(() => {
    if (open && !field) input.current?.focus();
  }, [open, field]);

  /*
   * Where the opened bar lies (the user, 2026-11-02: "roll it across all the header, keep the logos and then roll
   * the search all the way to the end"): from just after the LOGOS — MyRave's and, inside a rave, the rave's
   * (`.ui-header-brands`; the menu button when they are hidden) — to the END of the header bar, over the
   * messages, the bell and the account. `--find-reach` is its width, `--find-end` how far past its own icon it
   * runs to the bar's end. Measured, because what lies on either side is any width. Before paint, so the first
   * frame already animates toward it. Only the narrow-screen rules in tokens.css read them.
   */
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!shown || !el || field) return;
    const measure = () => {
      const header = el.closest('header');
      const bar = header?.querySelector<HTMLElement>('.ui-header-bar');
      const brands = header?.querySelector<HTMLElement>('.ui-header-brands');
      const toggle = header?.querySelector<HTMLElement>('.shell-toggle');
      const anchor = brands && brands.getBoundingClientRect().width > 0 ? brands : toggle;
      if (!anchor || !bar) return;
      const span = findSpan(el.getBoundingClientRect(), anchor.getBoundingClientRect(), bar.getBoundingClientRect(),
        getComputedStyle(el).direction === 'rtl');
      el.style.setProperty('--find-reach', `${span.reach}px`);
      el.style.setProperty('--find-end', `${span.end}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [shown]);

  const typed = q.trim();
  const term = useDebounced(typed, SEARCH_DEBOUNCE_MS);
  const live = term.length >= SEARCH_MIN_CHARS;

  /*
   * The same key and the same shape as the results page, so the page opened
   * with Enter starts from what the dropdown already fetched.
   */
  const results = useQuery({
    queryKey: ['search', term],
    queryFn: () => endpoints.search(term),
    enabled: live && Boolean(user) && !inRave && !inConsole && !onPage,
    staleTime: 30_000,
  });
  const consoleResults = useQuery({
    queryKey: ['console-search', term],
    queryFn: () => consoleScope!.search(term),
    enabled: live && inConsole,
    staleTime: 30_000,
  });
  const consoleOptions: Option[] = (live && inConsole ? consoleResults.data ?? [] : [])
    .map((hit) => ({ kind: 'console', id: hit.id, label: hit.label, detail: hit.detail, hit }));
  const inGroup = (g: ConsoleHit['group']) => consoleOptions.filter((o) => o.kind === 'console' && o.hit.group === g);

  const { raves, artists } = optionsOf(live && !inRave && !onPage ? results.data : undefined);
  /*
   * The rave's own results are searched on every keystroke, not debounced:
   * nothing is asked of a network, and the bundle is already in memory.
   */
  /*
   * The rave's `search` event, once a query has SETTLED (900 ms, ≥2 chars) —
   * the header is the rave's search now ("Search the lineup" left the menu,
   * 2026-09-28), so it records what that screen used to. Once per query.
   */
  const recorded = useRef('');
  useEffect(() => {
    if (!scope || !inRave || typed.length < SEARCH_MIN_CHARS || typed === recorded.current) return undefined;
    const t = setTimeout(() => { recorded.current = typed; scope.record(typed); }, 900);
    return () => clearTimeout(t);
  }, [scope, inRave, typed]);

  const local = useMemo(
    () => (scope && inRave && typed.length >= SEARCH_MIN_CHARS ? scope.search(typed) : { acts: [], stages: [] }),
    [scope, inRave, typed],
  );
  // A country or a genre the words name comes first — pressed, it opens every act of it (the 0.14.82 walk).
  const countries = (local.countries ?? []).map(localOption);
  const genres = (local.genres ?? []).map(localOption);
  const acts = local.acts.map(localOption);
  const stages = local.stages.map(localOption);
  // Places on the rave's map (2026-11-02) — last, after the programme.
  const places = (local.places ?? []).map(localOption);
  /*
   * The ways to search wider, always the LAST options so the arrow keys reach
   * them — and there with no results too, which is exactly when somebody
   * wants to look further. **Inside a rave the results open on the RAVE's tab**
   * (the user, 2026-11-02: "load results first on the tab of the current rave
   * as default") — "Search {rave} for …", which works offline; all of MyRave
   * is the tab beside it. Elsewhere, "Search all of MyRave" — not signed out:
   * the platform's search needs a session — nor where the dropdown already is it.
   */
  const toRave: Option[] = scope && (onPage || inRave)
    ? [{ kind: 'rave-page', id: 'scope-rave', label: tr('Search {name} for “{query}”', { name: scope.name, query: typed }), detail: null }]
    : [];
  const toAll: Option[] = !scope && user && (onPage || inConsole)
    ? [{ kind: 'all', id: 'all-myrave', label: tr('Search all of MyRave for “{query}”', { query: typed }), detail: null }]
    : [];
  const switcher: Option[] = typed.length < SEARCH_MIN_CHARS ? []
    : [
      ...(onPage
        ? consoleScope && !scope ? [{ kind: 'scope', id: 'scope-console', label: consoleScope.label ?? tr('Search the admin console'), detail: null, to: 'scope' } as Option] : []
        : page ? [{ kind: 'scope', id: 'scope-page', label: tr('Search only this page for “{query}”', { query: typed }), detail: null, to: 'page' } as Option] : []),
      ...toRave,
      ...toAll,
    ];
  const found = onPage ? []
    : inRave ? [...countries, ...genres, ...acts, ...stages, ...places]
      : inConsole ? (['sections', 'acts', 'stages', 'raves', 'ravers', 'artists'] as const).flatMap(inGroup)
        : [...raves, ...artists];
  const options = [...found, ...switcher];
  // Waiting: the debounce has not caught up with the field, or the answer
  // for this term has not landed.
  const waiting = !inRave && !onPage && typed.length >= SEARCH_MIN_CHARS
    && (typed !== term || (inConsole ? consoleResults.isLoading : results.isLoading));

  // A new answer is a new list; a highlight left over from the old one would
  // point at a different row.
  useEffect(() => { setActive(-1); }, [term, results.data, consoleResults.data, inRave, onPage]);

  /* On a page the list is only the way out — none to offer (signed out, no rave), no list over the page; with the
     results page open, the page is the list. */
  const listOpen = shown && focused && !dismissed && !everywhere && !ravePage && !(onPage && switcher.length === 0);

  /** Somewhere on `app.` — the router's if we are there, a real navigation if not. */
  const toApp = (path: string) => {
    if (isAppHost()) navigate(path);
    else void leaveTo(appUrl(path));
  };

  const close = () => { setDismissed(true); setActive(-1); };

  /** A results page — the rave's or all of MyRave's — for the field's words; the field keeps them. */
  const openResults = (where: 'rave' | 'all') => {
    if (onPage && page) { setOwn(typed); page.setQuery(''); }
    setChoice('scope');
    showRaveResults({ where, term: typed, filter: null });
    close();
    input.current?.blur();
  };
  const openEverywhere = () => openResults('all');
  const openRavePage = () => openResults('rave');
  const closeResults = () => showRaveResults(null);
  /* Typing while one is open searches again — a genre or a country chosen before gives way to the words. */
  useEffect(() => {
    if (resultsPage && typed.length >= SEARCH_MIN_CHARS && typed !== resultsPage.term) showRaveResults({ ...resultsPage, term: typed, filter: null });
  }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps
  /* Closed — by its ✕, Escape, Back or a result — the field is put away with it. */
  const hadResults = useRef(false);
  useEffect(() => {
    if (resultsPage) { hadResults.current = true; return; }
    if (!hadResults.current) return;
    hadResults.current = false;
    setChoice(null); setOwn(''); setOpen(false);
  }, [resultsPage]);
  /* The rave's page open: all of MyRave's is one tab away — fetched now, so the switch never waits. */
  useEffect(() => { if (ravePage && user) void loadSearchPage(); }, [ravePage, user]);
  /* Another page closes it — a result was opened, or the person went elsewhere. */
  const location = useLocation();
  useEffect(() => { showRaveResults(null); }, [location.pathname]);

  const choose = (o: Option) => {
    if (o.kind === 'scope') {
      // The same field, the same words, the other scope — the list stays open. Leaving the page shows it whole
      // again; coming back filters it by the words.
      if (onPage && page && o.to !== 'page') { setOwn(page.query); page.setQuery(''); }
      if (o.to === 'page' && page) page.setQuery(own);
      setChoice(o.to);
      setActive(-1);
      input.current?.focus();
      return;
    }
    close();
    input.current?.blur();
    if (o.kind === 'all') { openEverywhere(); return; }
    if (o.kind === 'rave-page') { openRavePage(); return; }
    if (o.kind === 'console') { setQ(''); o.hit.open(); return; }
    if (o.kind === 'local') scope?.open(o.hit);
    else if (o.kind === 'rave') void leaveTo(festivalUrl(o.subdomain, o.editionSlug));
    else toApp(`/artists/${o.slug}`);
  };

  const submit = () => {
    if (!typed) return;
    if (user) { openEverywhere(); return; }
    close();
    toApp(`/raves?q=${encodeURIComponent(typed)}`);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault();
        if (!listOpen) { setDismissed(false); return; }
        if (options.length === 0) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i < 0
          ? (step === 1 ? 0 : options.length - 1)
          : (i + step + options.length) % options.length));
        return;
      }
      case 'Enter': {
        e.preventDefault();
        const o = listOpen && active >= 0 ? options[active] : undefined;
        if (o) choose(o);
        // On a page — or the results page — the page is the result: put the keyboard away so it shows.
        else if (onPage || everywhere || ravePage) { close(); input.current?.blur(); }
        // In a rave Enter opens its results page — every match, not the dropdown's glance.
        else if (inRave && typed.length >= SEARCH_MIN_CHARS) openRavePage();
        // In a rave there is no results page to go to: Enter takes the best
        // match, as the highlight would have.
        else if (inRave || inConsole) { if (listOpen && options[0]) choose(options[0]); }
        else submit();
        return;
      }
      case 'Escape': {
        e.preventDefault();
        // First Escape closes the list (or the results page); a second one puts the field away.
        if (resultsPage) closeResults();
        else if (listOpen) close();
        else { setQ(''); setOpen(false); }
        return;
      }
      default:
    }
  };

  const optionId = (o: Option) => `${listId}-${o.id}`;
  const activeOption = listOpen && active >= 0 ? options[active] : undefined;

  const row = (o: Option, index: number) => (
    <div
      key={o.id}
      id={optionId(o)}
      role="option"
      aria-selected={index === active}
      className={o.kind === 'scope' || o.kind === 'all' || o.kind === 'rave-page' ? 'header-search-option header-search-switch' : 'header-search-option'}
      // `mousedown`, not `click`, and prevented: a click would blur the
      // field first, and the blur closes the list before the click lands.
      onMouseDown={(e) => { e.preventDefault(); choose(o); }}
      onMouseEnter={() => setActive(index)}
    >
      <span className="header-search-label">
        {o.kind === 'local' && o.hit.color && (
          // The stage's own colour — data, so the one inline value here.
          <span className="header-search-dot" style={{ background: o.hit.color }} aria-hidden="true" />
        )}
        {o.label}
        {o.kind === 'artist' && o.flags && <span className="header-search-flags">{o.flags}</span>}
        {o.kind === 'local' && o.hit.flags && <span className="header-search-flags">{o.hit.flags}</span>}
      </span>
      {o.detail && <span className="header-search-detail">{o.detail}</span>}
    </div>
  );

  const group = (key: string, label: string, list: Option[], offset: number) => list.length > 0 && (
    <div role="group" aria-labelledby={`${listId}-${key}`} className="header-search-group">
      <div id={`${listId}-${key}`} role="presentation" className="header-search-heading">{label}</div>
      {list.map((o, i) => row(o, offset + i))}
    </div>
  );

  /** What the field says it searches — this rave, or everything. */
  const fieldLabel = onPage && page ? page.label
    : inRave && scope
    ? tr('Search {name} — acts, artists, stages', { name: scope.name })
    : inConsole ? consoleScope?.label ?? tr('Search the admin console')
      : tr('Search raves and artists');

  /**
   * **One element that grows, not two that swap** — the field is uncovered
   * by the icon by animating WIDTH, with `overflow: hidden` as the moving
   * edge. Which is also why the dropdown is a SIBLING of `.shell-find` rather
   * than inside it: inside, that overflow would clip it to the bar.
   *
   * The icon stays a real `<button>` so a keyboard reaches the collapsed
   * control, and the input is out of the tab order while it is hidden.
   */
  return (
    <div ref={box} className="header-search" data-field={field || undefined}>
      <div className="shell-find" data-open={shown || undefined}>
        <button
          className="shell-search shell-find-icon"
          onClick={() => setOpen(true)}
          aria-label={fieldLabel}
          aria-expanded={shown}
          tabIndex={shown ? -1 : 0}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
          </svg>
        </button>

        <input
          ref={input}
          value={q}
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeOption ? optionId(activeOption) : undefined}
          onChange={(e) => { setQ(e.target.value); setDismissed(false); }}
          onKeyDown={onKeyDown}
          // Fetched while somebody types, so the results page is there the moment it is asked for.
          onFocus={() => { setFocused(true); setDismissed(false); if (user) void loadSearchPage(); }}
          // Collapses on blur only when nothing was typed — otherwise tabbing
          // to the clear button would throw away what somebody was typing.
          onBlur={() => { setFocused(false); if (!q.trim()) setOpen(false); }}
          placeholder={fieldLabel}
          aria-label={fieldLabel}
          aria-hidden={!shown}
          tabIndex={shown ? 0 : -1}
          className="shell-find-input"
        />

        {shown && q && (
          <button
            onClick={() => { setQ(''); input.current?.focus(); }}
            aria-label={tr('Clear the search')}
            className="app-iconfield-icon app-bare"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        )}
      </div>

      {everywhere && term.length >= SEARCH_MIN_CHARS && (
        <Suspense fallback={(
          // Loading the first time: the frame already covers, so the page underneath never shows.
          <ResultsPage eyebrow={tr('All of MyRave')} title={`“${term}”`} onClose={closeResults}><ResultsLoading label={tr('Searching…')} /></ResultsPage>
        )}>
          <SearchPage
            term={term}
            onClose={closeResults}
            openArtist={(slug) => { closeResults(); toApp(`/artists/${slug}`); }}
            // Inside a rave: its own search is the other tab — offline too.
            raveTab={scope ? { label: scope.name, onPick: () => showRaveResults({ where: 'rave', term: typed, filter: null }) } : undefined}
          />
        </Suspense>
      )}

      {listOpen && (
        <div id={listId} role="listbox" aria-label={fieldLabel} className="header-search-list">
          {typed.length < SEARCH_MIN_CHARS ? (
            // Idle: focused and (nearly) empty. Say what CAN be found, rather
            // than an empty box that makes somebody guess.
            <div role="presentation" className="header-search-note">
              {inRave && scope
                ? tr('Search {name} by act, artist, country or stage.', { name: scope.name })
                : inConsole ? consoleScope?.hint ?? tr('Search sections, raves, ravers and artists')
                  : tr('Search raves by name, or artists by name, country or genre')}
            </div>
          ) : (
            <>
              {/* A page filtered by the field IS the result: no "Nothing found" over it — nor over a row that searches
                  wider, which is the answer to nothing found (the user, 2026-11-02). */}
              {onPage ? null : waiting && found.length === 0 ? (
                <div role="presentation" className="header-search-note" aria-live="polite">{tr('Searching…')}</div>
              ) : found.length === 0 ? (switcher.length > 0 ? null :
                <div role="presentation" className="header-search-note" aria-live="polite">
                  {inRave && scope
                    ? tr('Nothing in {name} for “{query}”', { name: scope.name, query: typed })
                    : tr('Nothing found for “{query}”', { query: typed })}
                </div>
              ) : inRave ? (
                <>
                  {group('countries', tr('Countries'), countries, 0)}
                  {group('genres', tr('Genres'), genres, countries.length)}
                  {group('acts', tr('Acts'), acts, countries.length + genres.length)}
                  {group('stages', tr('Stages'), stages, countries.length + genres.length + acts.length)}
                  {group('places', tr('Places'), places, countries.length + genres.length + acts.length + stages.length)}
                </>
              ) : inConsole ? (
                <>
                  {(() => {
                    // Each group after the ones before it, in the order `found` lists them.
                    const order = [
                      ['sections', tr('Sections')], ['acts', tr('Acts')], ['stages', tr('Stages')],
                      ['raves', tr('Raves')], ['ravers', tr('Ravers')], ['artists', tr('Artists')],
                    ] as const;
                    let at = 0;
                    return order.map(([g, label]) => {
                      const list = inGroup(g);
                      const out = group(`c-${g}`, label, list, at);
                      at += list.length;
                      return <Fragment key={g}>{out}</Fragment>;
                    });
                  })()}
                </>
              ) : (
                <>
                  {group('raves', tr('Raves'), raves, 0)}
                  {group('artists', tr('Artists'), artists, raves.length)}
                </>
              )}
              {switcher.map((o, i) => row(o, found.length + i))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
